import "./layout.css";

import { useAtomValue } from "jotai";
import { useEffect } from "react";
import { Outlet } from "react-router";

import { DebugModeWidget } from "~/features/debug-mode/index.tsx";
import { GlobalHeader } from "~/shared/components/global-header/global-header.tsx";
import { LegalFooter } from "~/shared/components/legal-footer/legal-footer.tsx";
import { LoadingSpinner } from "~/shared/components/loading-spinner/loading-spinner.tsx";
import { NavBar } from "~/shared/components/navbar/index.tsx";
import { PublicLayout } from "~/shared/components/public-layout/public-layout.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import { useGlobalSpinner } from "~/shared/hooks/use-global-spinner.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import {
	enrichAnalyticsForExternalUser,
	enrichAnalyticsForInternalUser,
	enrichAnalyticsWithActiveCustomer,
} from "~/shared/utils/google-analytics-helper.ts";
import { setMonitoringUser } from "~/shared/utils/monitoring.ts";
import { useIsAuthenticated } from "~/shared/utils/use-is-authenticated.tsx";

export function Layout() {
	const { isAuthenticated, employeeId, userGuid, customerIds, fullName } =
		useIsAuthenticated();
	const { isLoading } = useGlobalSpinner();
	const { isInternal } = useDeploymentType();
	const selectedCustomer = useAtomValue(selectedCustomerAtom);
	const {
		isReady: isCustomerProfileSwitchReady,
		isEnabled: isCustomerProfileSwitchEnabled,
	} = useFeatureFlagGate("enableCustomerProfileSwitch");
	const shouldShowGlobalHeader =
		!isInternal &&
		isCustomerProfileSwitchReady &&
		isCustomerProfileSwitchEnabled;
	const { isEnabled: isOneTrustEnabled } = useFeatureFlagGate(
		"enableExternalTraxFooter",
	);

	// The OneTrust banner is hidden by default via a <style id="onetrust-gate"> in index.html.
	// When the flag is enabled, or for unauthenticated external users (public pages), remove that style so the banner can appear.
	useEffect(() => {
		if (isOneTrustEnabled || (!isAuthenticated && !isInternal)) {
			document.querySelector("#onetrust-gate")?.remove();
		}
	}, [isOneTrustEnabled, isAuthenticated, isInternal]);

	// The legal footer should only be shown once an external user is authenticated and the global spinner is done loading.
	const shouldShowLegalFooter =
		isAuthenticated && !isInternal && !isLoading && isOneTrustEnabled;

	useEffect(() => {
		if (isAuthenticated) {
			// Internal users have a numeric employeeId; external users have a userGuid instead
			if (employeeId && !Number.isNaN(Number(employeeId))) {
				enrichAnalyticsForInternalUser(employeeId);
				setMonitoringUser(employeeId, "internal", fullName);
			} else if (userGuid) {
				enrichAnalyticsForExternalUser(userGuid, customerIds);
				setMonitoringUser(userGuid, "customer", selectedCustomer?.name);
			}
		}
	}, [
		isAuthenticated,
		employeeId,
		userGuid,
		customerIds,
		selectedCustomer,
		fullName,
	]);

	// Push the user's currently-active customer profile id to GA once the profile is
	// resolved, and again whenever it changes. Internal users skip this. We do NOT push
	// during the bootstrap window while CustomerDataInitializer is hydrating the atom —
	// otherwise a transient 0 would be attributed to startup events and pollute GA.
	useEffect(() => {
		if (!isAuthenticated) return;
		if (employeeId && !Number.isNaN(Number(employeeId))) return;
		if (!selectedCustomer) return;
		enrichAnalyticsWithActiveCustomer(selectedCustomer.id);
	}, [isAuthenticated, employeeId, selectedCustomer]);

	return (
		<>
			{isAuthenticated ? (
				<>
					<LoadingSpinner isVisible={isLoading} />
					<NavBar>
						<>
							{shouldShowGlobalHeader ? <GlobalHeader /> : null}

							<div
								className={"layout-shell layout-shell-authenticated"}
								data-testid={"layout-shell-authenticated"}
							>
								<div className={"layout-main-content"}>
									<Outlet />
								</div>
								{shouldShowLegalFooter ? <LegalFooter /> : null}
							</div>
						</>
					</NavBar>
				</>
			) : (
				<PublicLayout>
					<div
						className={"layout-shell layout-shell-public"}
						data-testid={"layout-shell-public"}
					>
						<div className={"layout-main-content"}>
							<Outlet />
						</div>
						<LegalFooter />
					</div>
				</PublicLayout>
			)}
			<DebugModeWidget />
		</>
	);
}
